import { ObjectUtils, ArrayUtils } from "@feng3d/polyfill";
const __class__ = "__class__";
const _serialize__ = "_serialize__";
const _definitionCache = {};
function Serializable(className) {
  return (constructor) => {
    if (_definitionCache[className]) {
      console.warn(`重复定义类型 ${className}，${_definitionCache[className]} ${constructor} ！`);
    } else {
      const prototype = constructor.prototype;
      _definitionCache[className] = constructor;
      Object.defineProperty(prototype, __class__, { value: className, writable: true, enumerable: false });
    }
  };
}
function getClassName(instance) {
  let prototype;
  if (typeof instance === "function") {
    prototype = instance.prototype;
  } else {
    prototype = Object.getPrototypeOf(instance);
  }
  if (prototype.hasOwnProperty(__class__)) {
    return prototype[__class__];
  }
  console.warn(`名称为 ${prototype.constructor.name} 的 ${instance} 未注册，请使用 @Serializable 进行注册反序列化的类。`);
  return null;
}
function getInstance(classname, params) {
  let Cls = _definitionCache[classname];
  if (!Cls) {
    const definition = globalThis[classname];
    if (definition && typeof definition === "function" && definition["name"] === classname) {
      _definitionCache[classname] = definition;
      Cls = definition;
    }
  }
  console.assert(!!Cls, `${classname} 未注册，请使用 @Serializable 进行注册反序列化的类。`);
  let instance;
  if (Cls.__create__) {
    instance = Cls.__create__();
  } else {
    instance = new Cls();
  }
  $set(instance, params);
  return instance;
}
function $serialize(target, omitDefault = true) {
  return serialization.serialize(target, omitDefault);
}
function $deserialize(object) {
  return serialization.deserialize(object, true);
}
function $set(target, source) {
  return serialization.setValue(target, source);
}
function $diff(target, source) {
  return serialization.different(target, source, true);
}
function $clone(target) {
  return serialization.clone(target);
}
function propertyHandler(target, source, property, param) {
  const handlers = param.handlers;
  for (let i = 0; i < handlers.length; i++) {
    if (handlers[i](target, source, property, param)) {
      return true;
    }
  }
  return true;
}
class Serialization {
  constructor() {
    this.serializeHandlers = [];
    this.deserializeHandlers = [];
    this.differentHandlers = [];
    this.setValueHandlers = [];
  }
  /**
   * 序列化对象
   *
   * 过程中使用 different与默认值作比较减少结果中的数据。
   *
   * @param target 被序列化的对象
   * @param omitDefault 是否忽略默认值
   *
   * @returns 序列化后简单数据对象（由Object与Array组合可 JSON.stringify 的简单结构）
   */
  serialize(target, omitDefault = true) {
    const handlers = this.serializeHandlers.sort((a, b) => b.priority - a.priority).map((v2) => v2.handler);
    const param = {
      handlers,
      serialization: this,
      root: target,
      serializedMap: /* @__PURE__ */ new Map(),
      autoRefID: 1,
      omitDefault
    };
    const result = {};
    propertyHandler(result, { __root__: target }, rootKey, param);
    const v = result[rootKey];
    return v;
  }
  /**
   * 删除 Json 对象中 CLASS_KEY 属性，防止被反序列化。
   *
   * @param obj
   */
  deleteClassKey(obj) {
    if (ObjectUtils.isBaseType(obj))
      return;
    delete obj[__class__];
    for (const key in obj) {
      this.deleteClassKey(obj[key]);
    }
  }
  /**
   * 反序列化对象为基础对象数据（由Object与Array组合）
   *
   * @param object 换为Json的对象
   * @returns 反序列化后的数据
   */
  deserialize(object, omitDefault = true) {
    const handlers = this.deserializeHandlers.sort((a, b) => b.priority - a.priority).map((v2) => v2.handler);
    const param = { handlers, serialization: this, refs: {}, omitDefault };
    const result = {};
    propertyHandler(result, { __root__: object }, rootKey, param);
    const v = result[rootKey];
    Object.keys(param.refs).forEach((refid) => {
      const refs = param.refs[refid];
      const value = refs.target[refs.property];
      delete value[serializeIsRawKey];
      refs.refs.forEach((ref) => {
        ref.target[ref.property] = value;
      });
    });
    return v;
  }
  /**
   * 比较两个对象的不同，提取出不同的数据(可能会经过反序列化处理)
   *
   * @param target 用于检测不同的数据
   * @param source 模板（默认）数据
   * @param different 比较得出的不同（简单结构）数据
   *
   * @returns 比较得出的不同数据（由Object与Array组合可 JSON.stringify 的简单结构）
   */
  different(target, source, omitDefault = true) {
    const handlers = this.differentHandlers.sort((a, b) => b.priority - a.priority).map((v) => v.handler);
    const different = { __root__: {} };
    const param = { different, handlers, serialization: this, omitDefault };
    propertyHandler({ __root__: target }, { __root__: source }, rootKey, param);
    return different[rootKey];
  }
  /**
   * 从数据对象中提取数据给目标对象赋值（可能会经过序列化处理）
   *
   * @param target 目标对象
   * @param source 数据对象 可由Object与Array以及自定义类型组合
   */
  setValue(target, source, omitDefault = true) {
    if (ObjectUtils.isBaseType(source) || target === source)
      return target;
    const handlers = this.setValueHandlers.sort((a, b) => b.priority - a.priority).map((v) => v.handler);
    const param = { handlers, serialization: this, omitDefault };
    propertyHandler({ __root__: target }, { __root__: source }, rootKey, param);
    return target;
  }
  /**
   * 克隆
   * @param target 被克隆对象
   */
  clone(target) {
    return this.deserialize(this.serialize(target));
  }
}
function getSerializableMembers(object, serializableMembers) {
  serializableMembers = serializableMembers || [];
  if (object[protoKey]) {
    getSerializableMembers(object[protoKey], serializableMembers);
  }
  const serializePropertys = object[_serialize__];
  serializePropertys == null ? void 0 : serializePropertys.forEach((v) => {
    serializableMembers.push(v);
  });
  ArrayUtils.unique(serializableMembers);
  return serializableMembers;
}
const serialization = new Serialization();
serialization.serializeHandlers.push(
  // 基础类型
  {
    priority: 0,
    handler(target, source, property) {
      const spv = source[property];
      if (ObjectUtils.isBaseType(spv)) {
        target[property] = spv;
        return true;
      }
      return false;
    }
  },
  // 处理循环引用以及多次引用
  {
    priority: 0,
    handler(target, source, property, param) {
      const spv = source[property];
      const serializedMap = param.serializedMap;
      if (serializedMap.has(spv)) {
        const value = param.serializedMap.get(spv);
        const tpv = value.target[value.property];
        if (!ObjectUtils.isBaseType(tpv)) {
          if (tpv[serializeIsRawKey] === void 0) {
            tpv[serializeIsRawKey] = param.autoRefID++;
          }
          const newtpv = {};
          newtpv[serializeIsRefKey] = tpv[serializeIsRawKey];
          target[property] = newtpv;
          return true;
        }
      } else {
        serializedMap.set(spv, { target, property });
      }
      return false;
    }
  },
  // 处理方法
  {
    priority: 0,
    handler(target, source, property) {
      const spv = source[property];
      if (spv && typeof spv === "function") {
        const object = {};
        object[__class__] = "function";
        object.data = spv.toString();
        target[property] = object;
        return true;
      }
      return false;
    }
  },
  // 排除不支持序列化对象 Serializable === false 时不进行序列化
  {
    priority: 0,
    handler(target, source, property) {
      const spv = source[property];
      if (spv && spv.Serializable === false) {
        return true;
      }
      return false;
    }
  },
  // 自定义序列化函数
  {
    priority: 0,
    handler(target, source, property) {
      const spv = source[property];
      if (spv && spv.serialize) {
        const object = {};
        target[property] = object;
        object[__class__] = getClassName(spv);
        spv.serialize(object);
        return true;
      }
      return false;
    }
  },
  // 处理数组
  {
    priority: 0,
    handler(target, source, property, param) {
      const spv = source[property];
      if (Array.isArray(spv)) {
        const arr = target[property] || [];
        target[property] = arr;
        const keys = Object.keys(spv);
        keys.forEach((v) => {
          propertyHandler(arr, spv, v, param);
        });
        return true;
      }
      return false;
    }
  },
  // 处理普通Object
  {
    priority: 0,
    handler(target, source, property, param) {
      const spv = source[property];
      if (ObjectUtils.isObject(spv)) {
        const object = {};
        target[property] = object;
        const keys = Object.keys(spv);
        keys.forEach((key) => {
          propertyHandler(object, spv, key, param);
        });
        return true;
      }
      return false;
    }
  },
  // 使用默认序列化
  {
    priority: -1e4,
    handler(target, source, property, param) {
      const tpv = target[property];
      const spv = source[property];
      if (!param.omitDefault) {
        const object = {};
        target[property] = object;
        const className = getClassName(spv);
        const keys = getSerializableMembers(spv);
        keys.forEach((key) => {
          propertyHandler(object, spv, key, param);
        });
        object[__class__] = className;
        return true;
      }
      if (ObjectUtils.objectIsEmpty(tpv) || tpv.constructor !== spv.constructor) {
        const className = getClassName(spv);
        let inst = spv.constructor.inst;
        if (!inst) {
          inst = spv.constructor.inst = new spv.constructor();
        }
        if (!(inst instanceof spv.constructor)) {
          inst = spv.constructor.inst = new spv.constructor();
        }
        const diff = param.serialization.different(spv, inst);
        diff[__class__] = className;
        target[property] = diff;
      } else {
        const diff = param.serialization.different(spv, tpv);
        if (diff) {
          target[property] = diff;
        }
      }
      return true;
    }
  }
);
serialization.deserializeHandlers = [
  // 基础类型
  {
    priority: 0,
    handler(target, source, property) {
      const spv = source[property];
      if (ObjectUtils.isBaseType(spv)) {
        target[property] = spv;
        return true;
      }
      return false;
    }
  },
  // 处理循环引用以及多次引用，学习 node.js 优化循环引用
  /**
       * ```
       * var o = {};
          o.o = o;
          o.o1 = o;
          o.o2 = o;
  
          console.log(o);
       * ```
       */
  {
    priority: 0,
    handler(target, source, property, param) {
      const spv = source[property];
      const refs = param.refs;
      if (spv[serializeIsRefKey] !== void 0 || spv[serializeIsRawKey] !== void 0) {
        const refid = spv[serializeIsRefKey] || spv[serializeIsRawKey];
        const currentRef = refs[refid] = refs[refid] || { refs: [], target: null, property: null };
        if (spv[serializeIsRawKey]) {
          currentRef.target = target;
          currentRef.property = property;
        } else {
          currentRef.refs.push({ target, property });
          return true;
        }
      }
      return false;
    }
  },
  // 处理方法
  {
    priority: 0,
    handler(target, source, property) {
      const spv = source[property];
      if (spv && spv[__class__] === "function") {
        target[property] = Function(`return (${spv.data})`)();
        return true;
      }
      return false;
    }
  },
  // 处理非原生Object对象
  {
    priority: 0,
    handler(target, source, property) {
      const spv = source[property];
      if (!ObjectUtils.isObject(spv) && !Array.isArray(spv)) {
        target[property] = spv;
        return true;
      }
      return false;
    }
  },
  // 处理数组
  {
    priority: 0,
    handler(target, source, property, param) {
      const spv = source[property];
      if (Array.isArray(spv)) {
        const arr = target[property] || [];
        const keys = Object.keys(spv);
        keys.forEach((key) => {
          propertyHandler(arr, spv, key, param);
        });
        target[property] = arr;
        return true;
      }
      return false;
    }
  },
  // 处理 没有类名称标记的 普通Object
  {
    priority: 0,
    handler(target, source, property, param) {
      const tpv = target[property];
      const spv = source[property];
      if (ObjectUtils.isObject(spv) && ObjectUtils.objectIsEmpty(spv[__class__])) {
        let obj = {};
        if (tpv)
          obj = tpv;
        const keys = Object.keys(spv);
        keys.forEach((key) => {
          propertyHandler(obj, spv, key, param);
        });
        target[property] = obj;
        return true;
      }
      return false;
    }
  },
  // 处理自定义反序列化对象
  {
    priority: 0,
    handler(target, source, property) {
      const tpv = target[property];
      const spv = source[property];
      let inst = getInstance(spv[__class__]);
      if (inst && inst.deserialize) {
        if (tpv && tpv.constructor === inst.constructor) {
          inst = tpv;
        }
        const result = inst.deserialize(spv);
        if (result) {
          inst = result;
        }
        target[property] = inst;
        return true;
      }
      return false;
    }
  },
  // 处理自定义对象的反序列化
  {
    priority: -1e4,
    handler(target, source, property, param) {
      const tpv = target[property];
      const spv = source[property];
      let inst = getInstance(spv[__class__]);
      if (inst) {
        if (tpv && tpv.constructor === inst.constructor) {
          inst = tpv;
        }
        const keys = Object.keys(spv);
        keys.forEach((key) => {
          if (key !== __class__) {
            propertyHandler(inst, spv, key, param);
          }
        });
        target[property] = inst;
        return true;
      }
      console.warn(`未处理`);
      return false;
    }
  }
];
serialization.differentHandlers = [
  // 相等对象
  {
    priority: 0,
    handler(target, source, property) {
      if (target[property] === source[property]) {
        return true;
      }
      return false;
    }
  },
  // 目标数据为null时
  {
    priority: 0,
    handler(target, source, property, param) {
      if (ObjectUtils.objectIsEmpty(source[property])) {
        param.different[property] = param.serialization.serialize(target[property]);
        return true;
      }
      return false;
    }
  },
  // 基础类型
  {
    priority: 0,
    handler(target, source, property, param) {
      const tpv = target[property];
      if (ObjectUtils.isBaseType(tpv)) {
        param.different[property] = tpv;
        return true;
      }
      return false;
    }
  },
  // 数组
  {
    priority: 0,
    handler(target, source, property, param) {
      const different = param.different;
      const tpv = target[property];
      const spv = source[property];
      if (Array.isArray(tpv)) {
        const keys = Object.keys(tpv);
        const diff = [];
        const newParam = { different: diff, handlers: param.handlers, serialization: param.serialization, omitDefault: param.omitDefault };
        keys.forEach((key) => {
          propertyHandler(tpv, spv, key, newParam);
        });
        if (Object.keys(diff).length > 0) {
          different[property] = diff;
        }
        return true;
      }
      return false;
    }
  },
  // 不同对象类型
  {
    priority: 0,
    handler(target, source, property, param) {
      const tpv = target[property];
      const spv = source[property];
      if (spv.constructor !== tpv.constructor) {
        param.different[property] = param.serialization.serialize(tpv);
        return true;
      }
      return false;
    }
  },
  // 默认处理
  {
    priority: -1e4,
    handler(target, source, property, param) {
      const different = param.different;
      const tpv = target[property];
      const spv = source[property];
      let keys = getSerializableMembers(tpv);
      if (tpv.constructor === Object) {
        keys = Object.keys(tpv);
      }
      const diff = {};
      const newParam = { different: diff, handlers: param.handlers, serialization: param.serialization, omitDefault: param.omitDefault };
      keys.forEach((v) => {
        propertyHandler(tpv, spv, v, newParam);
      });
      if (Object.keys(diff).length > 0) {
        different[property] = diff;
      }
      return true;
    }
  }
];
serialization.setValueHandlers = [
  // 值相等时直接返回
  {
    priority: 0,
    handler(target, source, property, _handlers) {
      if (target[property] === source[property]) {
        return true;
      }
      return false;
    }
  },
  // 当原值等于null时直接反序列化赋值
  {
    priority: 0,
    handler(target, source, property, param) {
      const tpv = target[property];
      const spv = source[property];
      if (ObjectUtils.objectIsEmpty(tpv)) {
        target[property] = param.serialization.deserialize(spv);
        return true;
      }
      return false;
    }
  },
  // 处理简单类型
  {
    priority: 0,
    handler(target, source, property) {
      const spv = source[property];
      if (ObjectUtils.isBaseType(spv)) {
        target[property] = spv;
        return true;
      }
      return false;
    }
  },
  // 处理数组
  {
    priority: 0,
    handler(target, source, property, param) {
      const tpv = target[property];
      const spv = source[property];
      if (Array.isArray(spv)) {
        console.assert(!!tpv);
        const keys = Object.keys(spv);
        keys.forEach((key) => {
          propertyHandler(tpv, spv, key, param);
        });
        target[property] = tpv;
        return true;
      }
      return false;
    }
  },
  // 处理非 Object 类型数据
  {
    priority: 0,
    handler(target, source, property, param) {
      const spv = source[property];
      if (!ObjectUtils.isObject(spv)) {
        target[property] = param.serialization.deserialize(spv);
        return true;
      }
      return false;
    }
  },
  // 处理 Object 基础类型数据
  {
    priority: 0,
    handler(target, source, property, param) {
      const tpv = target[property];
      const spv = source[property];
      if (ObjectUtils.isObject(spv) && spv[__class__] === void 0) {
        console.assert(!!tpv);
        const keys = Object.keys(spv);
        keys.forEach((key) => {
          propertyHandler(tpv, spv, key, param);
        });
        target[property] = tpv;
        return true;
      }
      return false;
    }
  },
  // 处理自定义类型
  {
    priority: -1e4,
    handler(target, source, property, param) {
      const tpv = target[property];
      const spv = source[property];
      const targetClassName = getClassName(target[property]);
      if (targetClassName === spv[__class__]) {
        const keys = Object.keys(spv);
        keys.forEach((key) => {
          propertyHandler(tpv, spv, key, param);
        });
        target[property] = tpv;
      } else {
        target[property] = param.serialization.deserialize(spv);
      }
      return true;
    }
  }
];
[Float32Array, Float64Array, Int8Array, Int16Array, Int32Array, Uint8Array, Uint16Array, Uint32Array, Uint8ClampedArray].forEach((element) => {
  Serializable(element.name)(element);
  element.prototype["serialize"] = function(object) {
    object.value = Array.from(this);
    return object;
  };
  element.prototype["deserialize"] = function(object) {
    return new this.constructor(object.value);
  };
});
const serializeIsRawKey = "__ref__";
const serializeIsRefKey = "__cycle__";
const rootKey = "__root__";
const protoKey = "__proto__";
function SerializeProperty() {
  return (target, propertyKey) => {
    if (!Object.getOwnPropertyDescriptor(target, _serialize__)) {
      Object.defineProperty(target, _serialize__, { value: [] });
    }
    const serializePropertys = target[_serialize__];
    serializePropertys.push(propertyKey);
  };
}
function getConstructor(className) {
  if (typeof className === "function") {
    return className;
  }
  if (typeof className === "object") {
    return className.constructor;
  }
  const Constructor2 = _definitionCache[className];
  if (!Constructor2) {
    console.warn(`无法获取 ${className} 对应的构造函数，请使用 @RegisterComponent 进行注册！`);
  }
  return Constructor2;
}
export {
  $clone,
  $deserialize,
  $diff,
  $serialize,
  $set,
  Serializable,
  Serialization,
  SerializeProperty,
  __class__,
  _definitionCache,
  _serialize__,
  getClassName,
  getConstructor,
  getInstance,
  serialization
};
//# sourceMappingURL=index.js.map
